import React, { useState, useCallback } from "react";

const FAQS = [
  {
    q: "What areas do you serve?",
    a: `Our service area spans Northern Virginia, Washington, D.C., and the greater DMV region, encompassing key counties such as Fairfax, Arlington, Loudoun, and Prince William; Maryland’s Montgomery, Prince George’s, and Anne Arundel counties; and municipalities including the Town of Vienna, the City of Falls Church, and the City of Alexandria. We also serve the Hampton Roads area, including Virginia Beach, Norfolk, Chesapeake, and nearby communities.`,
  },
  {
    q: "How long does it take to receive a completed site or grading plan?",
    a: `Project timelines vary depending on scope, but most standard grading and site plans are completed within 6–8 weeks after receiving all required information.\n\n*This applies only to residential*`,
  },
  {
    q: "Do you work with homeowners, or only with builders/developers?",
    a: `We proudly work with all client types including homeowners, general contractors, and developers.`,
  },
  {
    q: "Can you help submit plans for permit approval?",
    a: `Yes. We prepare permit-ready drawings and offer assistance submitting plans to local jurisdictions for review.`,
  },
  {
    q: "What is included in a typical site development plan?",
    a: `A site development plan typically includes layout, setbacks, grading, erosion control measures, utility connections, and stormwater design all prepared in accordance with local jurisdictional requirements.`,
  },
  {
    q: "Can you help determine zoning or development potential for a property?",
    a: `Yes. While we do not provide legal zoning opinions, we review zoning codes and property characteristics to inform feasibility studies, required plan types, and possible development pathways.`,
  },
  {
    q: "Can you revise an existing plan set prepared by someone else?",
    a: `Yes, if you provide editable CAD files, we can assess and revise your existing site, grading, or utility plans to bring them into compliance or match updated project scopes.`,
  },
  {
    q: "What if my property is in a floodplain or has wetlands?",
    a: `If your property is located in a floodplain or contains wetlands, our team is fully equipped to manage these challenges. We collaborate with licensed environmental specialists and utilize FEMA flood maps, USGS topographic data, and state environmental guidelines to assess site conditions. Where necessary, we provide wetland delineation coordination, floodplain impact analysis, and engineered site plans that meet local, state, and federal regulatory requirements.`,
  },
];

function AccordionItem({ i, isOpen, onToggle, q, a }) {
  const panelId = `faq-panel-${i}`;
  const btnId = `faq-button-${i}`;

  return (
    <div className="border border-secundario/20 rounded-xl bg-primario/60">
      <button
        id={btnId}
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-4 px-4 sm:px-6 py-4 sm:py-5"
      >
        <h3 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-semibold font-primaria text-secundario text-left">
          {q}
        </h3>
        <span
          className={`shrink-0 inline-flex h-8 w-8 items-center justify-center rounded-full border border-secundario/30 text-secundario transition-transform ${
            isOpen ? "rotate-45" : "rotate-0"
          }`}
          aria-hidden="true"
          title={isOpen ? "Collapse" : "Expand"}
        >
          {/* Plus icon (rotates to ×) */}
          <svg
            className="h-4 w-4"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
          >
            <path d="M12 5v14M5 12h14" />
          </svg>
        </span>
      </button>

      <div
        id={panelId}
        role="region"
        aria-labelledby={btnId}
        className={`grid transition-all duration-300 ease-out ${
          isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 sm:px-6 pb-4 sm:pb-5">
            {a.split("\n").map((para, idx) => (
              <p
                key={idx}
                className={`font-secundaria text-sm sm:text-base lg:text-lg leading-relaxed text-secundario/90 ${
                  para.trim().startsWith("*") && para.trim().endsWith("*")
                    ? "italic"
                    : ""
                }`}
              >
                {para.replace(/\*/g, "")}
              </p>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Faqs() {
  const [open, setOpen] = useState(() => new Set()); // multi-open

  const toggle = useCallback((i) => {
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  }, []);

  return (
    <main className="min-h-screen bg-primario text-secundario">
      {/* Hero */}
      <section className="px-4 sm:px-6 md:px-8 py-14 sm:py-16 md:py-20 max-w-6xl mx-auto">
        <div className="text-center">
          <h1 className="font-primaria text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-extrabold">
            Frequently Asked Questions
          </h1>
          <p className="mt-3 sm:mt-4 font-secundaria text-sm sm:text-base md:text-lg text-secundario/85 max-w-2xl mx-auto">
            Find quick answers about our services, coverage, timelines, and
            process. If you don’t see your question here, reach out to our team.
          </p>
        </div>

        {/* Accent line */}
        <div className="mt-8 mx-auto h-px w-full max-w-2xl bg-secundario/20" />
      </section>

      {/* Accordion list */}
      <section className="px-4 sm:px-6 md:px-8 pb-16 sm:pb-20 max-w-4xl mx-auto space-y-3 sm:space-y-4">
        {FAQS.map((item, i) => (
          <AccordionItem
            key={i}
            i={i}
            q={item.q}
            a={item.a}
            isOpen={open.has(i)}
            onToggle={() => toggle(i)}
          />
        ))}

        {/* Footer help */}
        <div className="mt-8 text-center">
          <p className="font-secundaria text-sm sm:text-base lg:text-lg text-secundario/80">
            Still need help?{" "}
            <a
              href="/consultation"
              className="underline decoration-secundario/50 underline-offset-4 hover:decoration-secundario"
            >
              Contact our team
            </a>
            .
          </p>
        </div>
      </section>
    </main>
  );
}
